import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";

import { Bullets, LegalDoc, Mail, Sub } from "./Doc";
import type { LegalSection } from "./Doc";

const EFFECTIVE = "September 12, 2026";
const UPDATED = "September 12, 2026";

function PricingLink(): ReactElement {
  return (
    <Link to="/pricing" className="text-primary hover:underline">
      getchill.ai/pricing
    </Link>
  );
}

const SECTIONS: readonly LegalSection[] = [
  {
    id: "agreement",
    title: "The agreement",
    body: (
      <>
        <p>
          These Terms of Service ("Terms") are a contract between you and Chill,
          operated by Max Giraldo, a sole proprietor in Los Angeles, California
          ("Chill", "we", "us"). They govern your access to and use of the Chill
          service at getchill.ai, the sites we host for you, our APIs, and
          anything else we offer under the Chill name (the "Service").
        </p>
        <p>
          By creating an account, clicking through signup, or using the Service,
          you agree to these Terms and to our{" "}
          <Link
            to="/legal/$doc"
            params={{ doc: "privacy" }}
            className="text-primary hover:underline"
          >
            Privacy Policy
          </Link>
          . If you are agreeing on behalf of a company or other organization,
          you confirm you have authority to bind it, and "you" means that
          organization.
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    title: "Eligibility and your account",
    body: (
      <>
        <Bullets
          items={[
            "You must be at least 16 years old and able to form a binding contract.",
            "You must give accurate account information and keep it current.",
            "You are responsible for everything that happens under your account, including anything a team member or an agent you authorize does.",
            "Keep your sign-in credentials and any API keys secret. Tell us at hello@getchill.ai as soon as you suspect unauthorized access.",
            "One person or organization per account. Do not share a single account across unrelated businesses to avoid fees.",
          ]}
        />
        <p>
          You may not use the Service if we have previously terminated your
          account, or if US export or sanctions law prohibits us from serving
          you.
        </p>
      </>
    ),
  },
  {
    id: "plans",
    title: "Plans, credits, and payment",
    body: (
      <>
        <Sub>Plans and trials</Sub>
        <p>
          The Service is offered on a free plan and on paid subscriptions.
          Current plans, prices, credit allowances, and any free-trial terms are
          shown at <PricingLink />, and those published terms are part of this
          agreement. A free trial converts into a paid subscription at the end
          of the trial period unless you cancel before it ends.
        </p>
        <Sub>Credits</Sub>
        <p>
          Agent work is metered in credits. Each plan includes a monthly credit
          allowance, and you can buy one-time top-ups on paid plans.
        </p>
        <Bullets
          items={[
            "Monthly plan credits expire two months after they are issued, which means unused credits from one period remain spendable through the next one, and then expire.",
            "Included daily and usage-specific grants reset on their own schedule and never roll over.",
            "Purchased top-up credits expire twelve months after purchase.",
            "Credits have no cash value, are not transferable, cannot be redeemed for money, and are forfeited when a subscription ends.",
            "Credits are consumed as the agent does work, including on requests whose result you do not end up keeping. We do not refund credits for output you dislike, though we will look at it if the agent clearly malfunctioned.",
          ]}
        />
        <Sub>Billing</Sub>
        <p>
          Fees are charged in advance through our payment processor, Stripe, to
          the payment method you provide, and they renew automatically for the
          same term until you cancel. Fees exclude taxes; where we are required
          to collect sales tax, VAT, or similar, it is added at checkout. If a
          charge fails, we may retry it and may suspend the account until it
          clears.
        </p>
        <Sub>Refunds and price changes</Sub>
        <p>
          Fees are non-refundable except where the law requires otherwise. You
          may cancel at any time from your billing page; cancellation stops
          future charges and takes effect at the end of the current paid term,
          and does not refund fees already paid. We may change prices on at
          least 30 days' notice; a prepaid annual term is honored at the
          original price until it ends.
        </p>
        <Sub>Chargebacks</Sub>
        <p>
          If you dispute a charge with your bank instead of contacting us, we
          may suspend the account while the dispute is open. Email <Mail />{" "}
          first — most billing problems take one message to fix.
        </p>
      </>
    ),
  },
  {
    id: "domains",
    title: "Domains registered through Chill",
    body: (
      <>
        <p>
          You can ask the agent to check and buy a domain name. We act as a
          reseller: the domain is registered through our registrar partner and
          the relevant registry.
        </p>
        <Bullets
          items={[
            "You are the registrant. You must give accurate registrant contact details and keep them accurate — ICANN rules allow a domain to be suspended for false or unverified contact data.",
            "Domain purchases and renewals are charged to your saved payment method at the price quoted and confirmed before purchase. Quotes expire, and the price can change if a quote lapses.",
            "Domain fees are non-refundable once the registration is submitted, including if you later cancel Chill. Registries do not refund us.",
            "Domains auto-renew by default so your site does not go dark. If a renewal charge fails, the domain lapses rather than being renewed on credit, and a lapsed domain may be sold to someone else.",
            "Your registration is also subject to ICANN policy, the registry's terms for that top-level domain, and our registrar's terms, which can require disclosure of registration data.",
            "You may transfer a domain you own away from us, subject to registry transfer rules and any lock period. Ask us at hello@getchill.ai and we will release it.",
          ]}
        />
        <p>
          You are responsible for choosing a domain that does not infringe
          someone else's trademark, and for any dispute brought under the UDRP
          or similar policy.
        </p>
      </>
    ),
  },
  {
    id: "your-content",
    title: "Your content",
    body: (
      <>
        <p>
          "Your Content" means everything you provide or publish through the
          Service: text, photos, audio, video, business information, messages to
          the agent, and the resulting site. You own Your Content. We claim no
          ownership in it.
        </p>
        <p>
          You grant us a non-exclusive, worldwide, royalty-free license to host,
          store, reproduce, modify (for formatting and technical delivery),
          transmit, display, and distribute Your Content solely to operate the
          Service and deliver your site to your visitors. This license exists
          because hosting requires it, and it ends when you delete the content
          or your account, apart from copies in backups that age out on the
          schedule in our Privacy Policy.
        </p>
        <p>
          You promise you have the rights to Your Content — including rights to
          photographs, music, fonts, and logos you upload — and that publishing
          it does not infringe anyone's intellectual property, privacy, or
          publicity rights. You are responsible for the accuracy of what you
          publish, including prices, event details, and hours.
        </p>
        <p>
          If your site collects personal data from visitors (an RSVP, a contact
          form, a mailing list), you are the controller of that data and must
          have a lawful basis to collect it and tell visitors what you do with
          it.
        </p>
        <Sub>Feedback</Sub>
        <p>
          If you send us suggestions, we can use them without obligation or
          compensation. We will not identify you as the source without asking.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You may not use the Service to:</p>
        <Bullets
          items={[
            "Publish or transmit content that is illegal, infringing, defamatory, harassing, threatening, or that sexualizes minors.",
            "Send spam, bulk unsolicited messages, or anything that violates telecom or anti-spam rules including the TCPA and CAN-SPAM.",
            "Run phishing, malware, deceptive commerce, fake storefronts, or impersonate a person or business you do not represent.",
            "Attack, probe, overload, or circumvent the Service, its rate limits, its credit metering, or its authentication — including by scraping, automated account creation, or reselling agent capacity.",
            "Reverse-engineer the Service, or use it to build a competing product by extracting its prompts, models, or tooling.",
            "Attempt to make the agent produce content that violates these Terms or the usage policies of the model providers behind it.",
            "Store or publish payment card data, government identifiers, or health records on a Chill-hosted site.",
          ]}
        />
        <p>
          We may remove content or suspend an account that violates this section.
          Where the violation is serious — illegal content, an active attack,
          fraud — we may act immediately and without notice.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "How the AI agent works, and your responsibility for it",
    body: (
      <>
        <p>
          Chill works by having an AI agent read your message, decide what you
          meant, and change your site. This is genuinely useful and genuinely
          imperfect.
        </p>
        <Bullets
          items={[
            "The agent can misread a request, change more than you intended, or produce text or layout you would not have written. We send a confirmation after each change; review it.",
            "You can ask the agent to undo or correct any change, and your message history is retained for a year so a change can be traced.",
            "Output is not legal, financial, medical, or professional advice, and may be inaccurate. Do not publish agent-written claims about prices, availability, or safety without checking them.",
            "Similar requests can produce similar output for different customers; we make no claim that agent output is unique to you, and other customers may receive comparable results.",
            "You are the publisher of your site. Everything on it is your responsibility, whether you typed it or the agent wrote it.",
          ]}
        />
        <p>
          As between you and us, and to the extent output is capable of being
          owned, you own the output the agent produces for your site.
        </p>
      </>
    ),
  },
  {
    id: "channels",
    title: "SMS and email channels",
    body: (
      <>
        <p>
          SMS is optional. You opt in by texting the binding code shown in your
          account to our toll-free number, and that number receives
          transactional replies about the changes you request. Message frequency
          varies; message and data rates may apply. Reply STOP to opt out or
          HELP for help. Full details are on the{" "}
          <Link to="/legal/sms" className="text-primary hover:underline">
            SMS program page
          </Link>
          .
        </p>
        <p>
          Only bind a phone number you control. Do not bind a number belonging
          to someone who has not consented. Email requests are accepted from
          verified addresses on your account; treat those addresses as
          credentials, since anyone who can send from them can change your site.
        </p>
        <p>
          Carrier delivery is outside our control. We are not liable for
          messages that are delayed, filtered, or not delivered.
        </p>
      </>
    ),
  },
  {
    id: "vault",
    title: "Credentials you store in the vault",
    body: (
      <>
        <p>
          You may store third-party credentials (for example an AWS access key
          or a GitHub token) in your Chill vault so an agent can run commands
          with them on a Mac you have connected. By doing so you confirm you are
          entitled to use that credential for that purpose, and you accept that
          actions the agent takes with it are taken on your account with the
          provider concerned.
        </p>
        <Bullets
          items={[
            "Store the least-privileged credential that does the job. Chill can decrypt what you store; it is not a zero-knowledge password manager.",
            "You approve the first use of each credential on each Mac. Remove a credential or disconnect a Mac at any time from the app.",
            "You are responsible for costs, changes, or damage caused by commands run with your credentials, including by an agent that misread a request. Review what it did.",
            "Rotate credentials you stored if you stop using Chill or believe a Mac was compromised.",
          ]}
        />
      </>
    ),
  },
  {
    id: "api",
    title: "API and integrations",
    body: (
      <>
        <p>
          We may provide APIs, an MCP server, or other programmatic access.
          Those are part of the Service and are covered by these Terms. API use
          consumes credits on the same basis as any other agent work, is subject
          to rate limits, and may change or be deprecated with reasonable notice.
          Do not use API access to resell the Service or to build a general
          proxy for other people's requests.
        </p>
        <p>
          If you connect a third-party service, your use of it is governed by
          that provider's terms, and we are not responsible for it.
        </p>
      </>
    ),
  },
  {
    id: "availability",
    title: "Availability and changes to the Service",
    body: (
      <>
        <p>
          We aim for the Service to be available and correct, but we do not
          offer a service level agreement or an uptime guarantee. We perform
          maintenance, sometimes without notice, and dependencies we rely on
          (hosting, model, payment, carrier, registry) can fail in ways we
          cannot control.
        </p>
        <p>
          We may add, change, or remove features. If we discontinue a feature
          you rely on or shut down the Service entirely, we will give at least
          30 days' notice to active paid accounts, let you export your content,
          and refund the unused portion of any prepaid term.
        </p>
        <p>
          Features labeled beta, preview, or experimental are provided as-is,
          may change or disappear without notice, and are excluded from any
          commitment in these Terms.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "Suspension and termination",
    body: (
      <>
        <p>
          You may stop using the Service and cancel or delete your account at any
          time from your account settings.
        </p>
        <p>
          We may suspend or terminate your account if you materially breach these
          Terms, if your use creates legal risk or harms other customers, if
          payment fails and stays unpaid, or if we are required to by law. Except
          where the breach is serious or urgent, we will tell you what the
          problem is and give you a reasonable chance to fix it.
        </p>
        <p>
          After termination or cancellation, your content is retained for 30 days
          so you can export it, then permanently deleted. Sections that by their
          nature should survive — content ownership, fees owed, disclaimers,
          limitation of liability, indemnification, and governing law — survive
          termination.
        </p>
      </>
    ),
  },
  {
    id: "copyright",
    title: "Copyright complaints",
    body: (
      <>
        <p>
          If you believe content on a Chill-hosted site infringes your copyright,
          send a notice to <Mail /> with: your contact details; identification of
          the work; the URL of the material; a statement that you believe in good
          faith the use is unauthorized; a statement, under penalty of perjury,
          that your notice is accurate and that you are the owner or authorized
          to act for them; and your signature.
        </p>
        <p>
          We remove infringing material, notify the customer, and accept
          counter-notices. We terminate the accounts of repeat infringers.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: (
      <p>
        The Service is provided "as is" and "as available", without warranties of
        any kind, whether express, implied, or statutory, including
        merchantability, fitness for a particular purpose, title,
        non-infringement, and any warranty arising from course of dealing or
        usage of trade. We do not warrant that the Service will be uninterrupted,
        secure, or error-free, that agent output will be accurate or suitable for
        your purpose, or that data will not be lost. Some jurisdictions do not
        allow the exclusion of implied warranties, so parts of this section may
        not apply to you.
      </p>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, neither party is liable for
          indirect, incidental, special, consequential, exemplary, or punitive
          damages, or for lost profits, lost revenue, lost business, or lost or
          corrupted data, even if advised such damages were possible.
        </p>
        <p>
          To the maximum extent permitted by law, our total aggregate liability
          arising out of or relating to the Service or these Terms is limited to
          the greater of the fees you paid us in the 12 months before the event
          giving rise to the claim, or one hundred US dollars.
        </p>
        <p>
          These limits do not apply to liability that cannot be excluded by law,
          including for fraud, willful misconduct, or death or personal injury
          caused by negligence. If you are a consumer, nothing in these Terms
          removes rights your local consumer law gives you.
        </p>
        <p>
          These limits are a fundamental part of the bargain and apply even if a
          limited remedy fails of its essential purpose.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnification",
    body: (
      <p>
        You will defend, indemnify, and hold us harmless from third-party claims,
        damages, losses, and reasonable legal fees arising out of Your Content,
        your website, your domain registrations, your use of the Service in
        breach of these Terms, or your violation of law or of someone else's
        rights. We will tell you promptly about any such claim, let you control
        the defense of claims that are solely about money, and cooperate at your
        expense. You may not settle a claim in a way that admits fault on our
        behalf without our consent.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms. For material changes, we will email account
        holders at least 14 days before they take effect. Other changes take
        effect when posted. If you do not accept a change, cancel before it takes
        effect; continuing to use the Service afterwards means you accept the new
        Terms. The effective and last-updated dates at the top of this page tell
        you which version is current.
      </p>
    ),
  },
  {
    id: "law",
    title: "Governing law and disputes",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of California,
          without regard to its conflict-of-laws rules, and the United Nations
          Convention on Contracts for the International Sale of Goods does not
          apply.
        </p>
        <p>
          Before filing anything, email <Mail /> and give us 30 days to resolve
          it. Most disputes end there. If that fails, you and we agree to the
          exclusive jurisdiction of the state and federal courts in Los Angeles
          County, California, and each of us consents to personal jurisdiction
          there. If you are a consumer resident in the EEA or UK, this does not
          deprive you of the protection of the mandatory law of your country of
          residence or of the right to sue in your local courts.
        </p>
        <p>
          There is no arbitration clause and no class-action waiver in these
          Terms.
        </p>
      </>
    ),
  },
  {
    id: "general",
    title: "General",
    body: (
      <Bullets
        items={[
          "Entire agreement. These Terms, the Privacy Policy, the SMS program page, and the plan terms published at getchill.ai/pricing are the whole agreement between us about the Service, and replace any earlier understanding.",
          "Severability. If a provision is held unenforceable, it is limited to the minimum extent necessary and the rest stays in force.",
          "No waiver. Not enforcing a right on one occasion does not waive it.",
          "Assignment. You may not assign these Terms without our written consent; we may assign them to a successor in a merger, acquisition, or sale of assets.",
          "Force majeure. Neither party is liable for delays caused by events beyond its reasonable control.",
          "Notices. We send notices to the email on your account; you send notices to hello@getchill.ai. Email counts as writing.",
          "Independent parties. Nothing here creates a partnership, agency, or employment relationship, and there are no third-party beneficiaries.",
        ]}
      />
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Chill · Max Giraldo, sole proprietor · Los Angeles, California, United
        States · <Mail />
      </p>
    ),
  },
];

export function Terms(): ReactElement {
  return (
    <LegalDoc
      doc="terms"
      title="Terms of Service"
      effective={EFFECTIVE}
      updated={UPDATED}
      summary={
        <>
          <p>
            The short version: pay for your plan, own your content, do not
            publish anything illegal, and check what the agent changed before
            you rely on it. We host your site, we do not claim it.
          </p>
          <p className="mt-4">
            The long version is below, and it is the one that governs.
          </p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
